import {z} from 'zod'
import {SUBJECT_MAX_LENGTH} from '@/features/mailer/constants'

// --- Recipient filter types (BE contract OPSSTD-3643) ---

export const MailerFilterField = z.object({
    key: z.string(),
    operator: z.string(),
    value: z.unknown()
})
export type MailerFilterField = z.infer<typeof MailerFilterField>

export const MailerFilters = z.object({
    operator: z.literal('and'),
    fields: z.array(MailerFilterField)
})
export type MailerFilters = z.infer<typeof MailerFilters>

export const MailerRecipients = z.object({
    patients: z.object({filters: MailerFilters.optional()}).optional(),
    consultants: z.object({filters: MailerFilters.optional()}).optional()
})
export type MailerRecipients = z.infer<typeof MailerRecipients>

// --- Form value enums ---

export const PatientStatusFilter = z.enum(['all', 'active', 'inactive'])
export type PatientStatusFilter = z.infer<typeof PatientStatusFilter>

// Serialized as BE key `consent` (OPSSTD-3648). Multiselect: empty array = "tutte" (no filter).
export const PatientConsentFilter = z.enum(['orientation', 'notifications', 'survey'])
export type PatientConsentFilter = z.infer<typeof PatientConsentFilter>

// Serialized as BE key `sector`; UI values map to PatientSectors arrays (OPSSTD-3648).
export const PatientSectorFilter = z.enum(['all', 'ambulatorial', 'home'])
export type PatientSectorFilter = z.infer<typeof PatientSectorFilter>

export const PatientServiceType = z.enum(['all', 'adults', 'evolutionary_age'])
export type PatientServiceType = z.infer<typeof PatientServiceType>

// Multiselect: empty array = "tutti" (no filter).
export const ConsultantStatusFilter = z.enum(['operative', 'candidate', 'suspended', 'absent', 'terminated'])
export type ConsultantStatusFilter = z.infer<typeof ConsultantStatusFilter>

export const ConsultantGroupFilter = z.enum(['all', 'referring_doctor', 'therapist'])
export type ConsultantGroupFilter = z.infer<typeof ConsultantGroupFilter>

// Multiselect: empty array = "tutti" (no filter).
export const ConsultantContractTypeFilter = z.enum(['consultant', 'flat_rate', 'employee'])
export type ConsultantContractTypeFilter = z.infer<typeof ConsultantContractTypeFilter>

export const ConsultantWorkSectorFilter = z.enum(['all', 'outpatient', 'domiciliary'])
export type ConsultantWorkSectorFilter = z.infer<typeof ConsultantWorkSectorFilter>

// --- Form validation schema ---

// Plain schema on purpose: z.preprocess would swallow these check errors
// whenever a sibling field is dirty (zod 3.22 quirk with ZodEffects merging).
// Empty-input normalization ('' → undefined) happens in the RHF register
// via `setValueAs` (see PatientFiltersPanel).
const optionalAge = z
    .number({invalid_type_error: 'Età non valida'})
    .int("L'età deve essere un numero intero")
    .min(0, "L'età non può essere negativa")
    .max(100, "L'età non può superare 100")
    .optional()

export const MailerFormSchema = z
    .object({
        subject: z
            .string()
            .min(1, "L'oggetto è obbligatorio")
            .max(SUBJECT_MAX_LENGTH, `L'oggetto non può superare i ${SUBJECT_MAX_LENGTH} caratteri`),
        body: z.string().min(1, 'Il corpo del messaggio è obbligatorio'),
        includePatients: z.boolean(),
        includeConsultants: z.boolean(),
        // Patient filters
        patientStatus: PatientStatusFilter,
        patientConsent: z.array(PatientConsentFilter),
        patientSector: PatientSectorFilter,
        patientServiceType: PatientServiceType,
        diagnosisEnabled: z.boolean(),
        neuromuscularDiagnosisIds: z.array(z.string()),
        priEnabled: z.boolean(),
        rehabPathology1Ids: z.array(z.string()),
        rehabPathology2Enabled: z.boolean(),
        rehabPathology2Ids: z.array(z.string()),
        otherPathology1Ids: z.array(z.string()),
        otherPathology2Enabled: z.boolean(),
        otherPathology2Ids: z.array(z.string()),
        patientMinAge: optionalAge,
        patientMaxAge: optionalAge,
        patientGender: z.enum(['all', 'male', 'female']),
        patientAslDistrictIds: z.array(z.string()),
        patientOutsideRegion: z.boolean(),
        // Consultant ("Personale") filters
        consultantStatus: z.array(ConsultantStatusFilter),
        consultantGroup: ConsultantGroupFilter,
        consultantContractType: z.array(ConsultantContractTypeFilter),
        consultantSpecializationIds: z.array(z.string()),
        consultantWorkSector: ConsultantWorkSectorFilter
    })
    .refine(data => data.includePatients || data.includeConsultants, {
        path: ['includePatients'],
        message: 'Seleziona almeno un tipo di destinatario'
    })
    .refine(
        data => {
            // On dirty parses (e.g. subject still empty) zod hands refinements the
            // RAW input, so empty ages arrive as NaN instead of undefined:
            // normalize again before comparing.
            const min = Number(data.patientMinAge)
            const max = Number(data.patientMaxAge)
            return Number.isNaN(min) || Number.isNaN(max) || min <= max
        },
        {
            path: ['patientMaxAge'],
            message: "L'età minima non può superare l'età massima"
        }
    )
export type MailerFormValues = z.infer<typeof MailerFormSchema>

export const MAILER_FORM_DEFAULTS: MailerFormValues = {
    subject: '',
    body: '',
    includePatients: false,
    includeConsultants: false,
    patientStatus: 'all',
    patientConsent: [],
    patientSector: 'all',
    patientServiceType: 'all',
    diagnosisEnabled: false,
    neuromuscularDiagnosisIds: [],
    priEnabled: false,
    rehabPathology1Ids: [],
    rehabPathology2Enabled: false,
    rehabPathology2Ids: [],
    otherPathology1Ids: [],
    otherPathology2Enabled: false,
    otherPathology2Ids: [],
    patientMinAge: undefined,
    patientMaxAge: undefined,
    patientGender: 'all',
    patientAslDistrictIds: [],
    patientOutsideRegion: false,
    consultantStatus: [],
    consultantGroup: 'all',
    consultantContractType: [],
    consultantSpecializationIds: [],
    consultantWorkSector: 'all'
}

// --- API request/response types ---

export const PreviewCounts = z.object({
    patients: z.number(),
    consultants: z.number(),
    total: z.number()
})
export type PreviewCounts = z.infer<typeof PreviewCounts>

export const PreviewSampleRecipient = z.object({
    name: z.string(),
    email: z.string(),
    kind: z.enum(['patient', 'consultant'])
})
export type PreviewSampleRecipient = z.infer<typeof PreviewSampleRecipient>

export const PreviewRecipientsResponse = z.object({
    counts: PreviewCounts,
    sample: z.array(PreviewSampleRecipient)
})
export type PreviewRecipientsResponse = z.infer<typeof PreviewRecipientsResponse>

// Paginated recipients browser per group (OPSSTD-3649).
export const MailerRecipientGroup = z.enum(['patients', 'consultants'])
export type MailerRecipientGroup = z.infer<typeof MailerRecipientGroup>

export const MailerRecipientListItem = PreviewSampleRecipient.extend({entityId: z.string()})
export type MailerRecipientListItem = z.infer<typeof MailerRecipientListItem>

export const TestSendResponse = z.object({
    sentTo: z.string()
})
export type TestSendResponse = z.infer<typeof TestSendResponse>

export const SendMailResponse = z.object({
    recipientCount: z.number()
})
export type SendMailResponse = z.infer<typeof SendMailResponse>

// --- History (batches + logs) ---

export const MailLogStatus = z.enum(['pending', 'sent', 'bounced', 'failed'])
export type MailLogStatus = z.infer<typeof MailLogStatus>

export const MailRecipientKind = z.enum(['patient', 'consultant', 'whitelist'])
export type MailRecipientKind = z.infer<typeof MailRecipientKind>

export const MailBatchTriggeredBy = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string()
})
export type MailBatchTriggeredBy = z.infer<typeof MailBatchTriggeredBy>

export const MailBatchSummary = z.object({
    batchId: z.string().uuid(),
    subject: z.string(),
    triggeredBy: MailBatchTriggeredBy.nullable(),
    totalRecipients: z.number(),
    counts: z.object({
        sent: z.number(),
        failed: z.number(),
        bounced: z.number(),
        pending: z.number()
    }),
    createdAt: z.string(),
    lastActivityAt: z.string()
})
export type MailBatchSummary = z.infer<typeof MailBatchSummary>

export const MailLog = z.object({
    id: z.string().uuid(),
    batchId: z.string().uuid(),
    triggeredByUserId: z.string().nullable(),
    subject: z.string(),
    recipient: z.string(),
    recipientKind: MailRecipientKind,
    recipientEntityId: z.string().nullable(),
    messageId: z.string().nullable(),
    status: MailLogStatus,
    attempts: z.number(),
    error: z.string().nullable(),
    sentAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string()
})
export type MailLog = z.infer<typeof MailLog>

// Synthetic campaign status derived FE-side from batch counts (contract §5).
export type MailBatchSyntheticStatus = 'sent' | 'partial' | 'sending'
